/**
 * MSSQL-specific database queries
 */

/** Escapes a value for embedding in a T-SQL single-quoted string literal. */
const quote = (value: string): string => value.replace(/'/g, "''");

/**
 * Builds a per-table catalog query that SQL Server can serve from ONE cached
 * plan for every table, instead of compiling a fresh one per table.
 *
 * Inlining the identifiers gave each of a database's tables its own distinct
 * query text, so the optimizer compiled every one: at 76 tables x the 9 catalog
 * queries below that is 684 compilations where 9 would do. (A schema compare
 * only reaches 6 of the 9 per table — `tableDefaultConstraints`,
 * `tableTriggers` and `tableStatistics` are Object-Explorer surfaces — but the
 * wrapper is the same for all nine, so quote whichever count the context is
 * about and don't mix them.) Measured against a real instance with a cleared
 * plan cache, the whole sweep ran in 32.8 s inlined and 1.3 s through
 * `sp_executesql` — 24.7x — with identical result sets (108/108 row-level
 * comparisons) and the cached-plan count dropping from 154 to 4. Cold-vs-warm
 * cost per query was already known to differ ~50x, which is the same fact seen
 * from the other side (#114, #115).
 *
 * `sp_executesql` is used rather than a driver-level parameter channel because
 * the whole statement is still a single SQL string: the engine-agnostic
 * `executeQuery(connectionId, database, sql)` seam every provider implements
 * needs no new parameter argument, so this stays contained in the query layer.
 * The OUTER call carries the values as literals — cheap to compile, and never
 * cached — while the INNER text is identical across tables and is what gets the
 * plan. `sysname` is `nvarchar(128)`, exactly the maximum identifier length.
 *
 * SECOND, EQUALLY LOAD-BEARING PROPERTY — the identifiers travel as `sysname`
 * (nvarchar) parameters spelled `N'…'`. An UNPREFIXED `'…'` literal is varchar:
 * SQL Server reads it in the DATABASE's default collation code page, so on any
 * non-`_UTF8` collation (every pre-2019 instance, and most since) a character
 * outside that code page degrades to `?` before the comparison happens. A table
 * named `注文` then matches nothing and the query returns ZERO rows — not an
 * error, an empty result that reads downstream as "this table has no columns".
 * Measured on Azure SQL Edge 15.0.2000.1574 / `SQL_Latin1_General_CP1_CI_AS`:
 * the varchar form returned 0 rows for `注文`, the `sysname`-parameter form
 * returned its 2 columns. That is why `getQueriesForEngine('mssql')` in
 * `electron/mcp-schema-introspection/schemaIntrospection.ts` reuses THIS
 * helper rather than keeping its own hand-mirrored WHERE clauses — the MCP copy
 * had exactly that bug for as long as it did (#115 follow-up).
 *
 * Bodies must therefore reference `@schema` and `@table` instead of
 * interpolating. Values are still escaped: a quote in an identifier would
 * otherwise break out of the outer literal.
 */
export const perTableQuery = (body: string) => {
  const inner = quote(body);
  return (schema: string, tableName: string): string =>
    `EXEC sp_executesql N'${inner}', N'@schema sysname, @table sysname', ` +
    `@schema = N'${quote(schema)}', @table = N'${quote(tableName)}';`;
};


/**
 * A table-scoped catalog body is written ONCE against a `TableScope` and
 * rendered two ways:
 *
 *  - per table — `@schema` / `@table` sysname parameters, wrapped in
 *    `sp_executesql` by `perTableQuery` (Object Explorer, Table Explorer, the
 *    schema-compare fallback); `PER_TABLE_SCOPE` renders exactly the text that
 *    existed before the bulk variant did, so every consumer that matches on
 *    it (the mock engine's matchers among them) sees no change;
 *  - in bulk — joined to a `VALUES` row constructor of the requested tables,
 *    one query per chunk, every row carrying the owning table's identity so
 *    rows group back per table (`mssqlBulkTableQueries`, S3).
 *
 * Same SELECT list, same joins, same predicate shape; only WHERE the table
 * identity comes from differs. That is the whole point of the indirection: a
 * column added to one variant cannot be forgotten on the other, and the bulk
 * path feeds the unchanged row→model mapping in `metadataService`.
 */
type TableScope = {
  /** Extra SELECT columns carrying the owning table's identity (bulk only). */
  select: string;
  /** Extra FROM-clause source placed just before WHERE (bulk only). */
  from: string;
  schemaRef: string;
  tableRef: string;
  /** Leading ORDER BY keys grouping rows per table (bulk only). */
  orderBy: string;
};

const PER_TABLE_SCOPE: TableScope = {
  select: '',
  from: '',
  schemaRef: '@schema',
  tableRef: '@table',
  orderBy: '',
};

export type MssqlTableIdentity = { schema: string; name: string };

/**
 * `(N'schema', N'name'),…` for a `VALUES` row constructor. `N'…'` for the same
 * reason the per-table builders bind `sysname` parameters (see `perTableQuery`):
 * an unprefixed literal is varchar and degrades a non-ASCII identifier to `?`.
 * Only `'` is significant inside the literal; `]` and every other character
 * travel as-is. SQL Server caps a row constructor at 1000 rows — callers chunk
 * well inside that.
 */
export function tableValuesClause(tables: readonly MssqlTableIdentity[]): string {
  return tables.map((t) => `(N'${quote(t.schema)}', N'${quote(t.name)}')`).join(',');
}

function bulkTableScope(tables: readonly MssqlTableIdentity[]): TableScope {
  return {
    select: '\n      v.table_schema AS table_schema,\n      v.table_name AS table_name,',
    from: `CROSS JOIN (VALUES ${tableValuesClause(tables)}) AS v(table_schema, table_name)\n    `,
    schemaRef: 'v.table_schema',
    tableRef: 'v.table_name',
    orderBy: 'v.table_schema, v.table_name, ',
  };
}

const columnsBody = (scope: TableScope): string => `
    SELECT${scope.select}
      c.name AS column_name,
      t.name AS data_type,
      -- For a user-defined ALIAS type (CREATE TYPE dbo.Name FROM nvarchar(50))
      -- resolve the underlying base system type so cross-engine type mapping has
      -- a portable token. CLR/assembly types are left as-is (no scalar base).
      CASE WHEN t.is_user_defined = 1 AND t.is_assembly_type = 0 THEN bt.name ELSE t.name END AS base_data_type,
      c.max_length,
      c.precision,
      c.scale,
      c.is_nullable,
      c.is_identity,
      CASE WHEN pk.column_id IS NOT NULL THEN 1 ELSE 0 END AS is_primary_key,
      dc.definition AS default_value,
      c.column_id AS ordinal_position
    FROM
      sys.columns c
    JOIN
      sys.types t ON c.user_type_id = t.user_type_id
    LEFT JOIN
      sys.types bt ON bt.user_type_id = t.system_type_id AND bt.is_user_defined = 0
    JOIN
      sys.objects o ON c.object_id = o.object_id
    JOIN
      sys.schemas s ON o.schema_id = s.schema_id
    LEFT JOIN
      sys.default_constraints dc ON c.default_object_id = dc.object_id
    LEFT JOIN (
      SELECT ic.object_id, ic.column_id
      FROM sys.index_columns ic
      JOIN sys.indexes i ON ic.object_id = i.object_id AND ic.index_id = i.index_id
      WHERE i.is_primary_key = 1
    ) pk ON c.object_id = pk.object_id AND c.column_id = pk.column_id
    ${scope.from}WHERE
      s.name = ${scope.schemaRef}
      AND o.name = ${scope.tableRef}
      AND o.type IN ('U', 'V')
    ORDER BY
      ${scope.orderBy}c.column_id;
  `;

/** `index_type` is the kind (`sys.indexes.type_desc`) of the index behind a
 *  PRIMARY KEY / UNIQUE constraint — part of the constraint's definition
 *  (`PRIMARY KEY NONCLUSTERED`), and `tableIndexes` excludes the primary key's
 *  index on purpose, so this is the only place it is reported. NULL for CHECK
 *  and FOREIGN KEY rows. */
const tableConstraintsBody = (scope: TableScope): string => `
    SELECT${scope.select}
      tc.CONSTRAINT_NAME AS constraint_name,
      tc.CONSTRAINT_TYPE AS constraint_type,
      kcu.COLUMN_NAME AS column_name,
      kcu.ORDINAL_POSITION AS column_ordinal,
      rc.UNIQUE_CONSTRAINT_NAME AS referenced_constraint,
      rkcu.TABLE_SCHEMA AS referenced_schema,
      rkcu.TABLE_NAME AS referenced_table,
      rkcu.COLUMN_NAME AS referenced_column,
      rc.UPDATE_RULE AS update_action,
      rc.DELETE_RULE AS delete_action,
      cc.definition AS check_definition,
      ki.type_desc AS index_type
    FROM
      INFORMATION_SCHEMA.TABLE_CONSTRAINTS tc
    LEFT JOIN
      INFORMATION_SCHEMA.KEY_COLUMN_USAGE kcu
      ON tc.CONSTRAINT_NAME = kcu.CONSTRAINT_NAME AND tc.TABLE_SCHEMA = kcu.TABLE_SCHEMA
    LEFT JOIN
      INFORMATION_SCHEMA.REFERENTIAL_CONSTRAINTS rc
      ON tc.CONSTRAINT_NAME = rc.CONSTRAINT_NAME
      AND tc.CONSTRAINT_SCHEMA = rc.CONSTRAINT_SCHEMA
    LEFT JOIN
      INFORMATION_SCHEMA.KEY_COLUMN_USAGE rkcu
      ON rc.UNIQUE_CONSTRAINT_NAME = rkcu.CONSTRAINT_NAME
      AND rc.UNIQUE_CONSTRAINT_SCHEMA = rkcu.TABLE_SCHEMA
      AND kcu.ORDINAL_POSITION = rkcu.ORDINAL_POSITION
    LEFT JOIN
      sys.check_constraints cc
      ON cc.name = tc.CONSTRAINT_NAME
      AND cc.schema_id = SCHEMA_ID(tc.CONSTRAINT_SCHEMA)
    LEFT JOIN
      sys.key_constraints kc
      ON kc.name = tc.CONSTRAINT_NAME
      AND kc.schema_id = SCHEMA_ID(tc.CONSTRAINT_SCHEMA)
    LEFT JOIN
      sys.indexes ki
      ON ki.object_id = kc.parent_object_id
      AND ki.index_id = kc.unique_index_id
    ${scope.from}WHERE
      tc.TABLE_SCHEMA = ${scope.schemaRef}
      AND tc.TABLE_NAME = ${scope.tableRef}
    ORDER BY
      ${scope.orderBy}tc.CONSTRAINT_TYPE, tc.CONSTRAINT_NAME, kcu.ORDINAL_POSITION;
  `;

const tableIndexesBody = (scope: TableScope): string => `
    SELECT${scope.select}
      i.name AS index_name,
      COL_NAME(ic.object_id, ic.column_id) AS column_name,
      i.is_unique,
      i.type_desc AS index_type,
      i.filter_definition AS filter_definition,
      ic.is_included_column AS is_included,
      ic.key_ordinal
    FROM
      sys.indexes i
    JOIN
      sys.index_columns ic ON i.object_id = ic.object_id AND i.index_id = ic.index_id
    JOIN
      sys.tables t ON i.object_id = t.object_id
    JOIN
      sys.schemas s ON t.schema_id = s.schema_id
    ${scope.from}WHERE
      s.name = ${scope.schemaRef}
      AND t.name = ${scope.tableRef}
      AND i.is_primary_key = 0
    ORDER BY
      ${scope.orderBy}i.name, ic.is_included_column, ic.key_ordinal, ic.index_column_id;
  `;

const tableOptionsBody = (scope: TableScope): string => `
    SELECT${scope.select}
      t.is_memory_optimized,
      t.durability_desc,
      (SELECT TOP 1 data_compression_desc FROM sys.partitions WHERE object_id = t.object_id) AS data_compression_desc,
      (SELECT TOP 1 fill_factor FROM sys.indexes WHERE object_id = t.object_id AND fill_factor > 0) AS fill_factor,
      (SELECT TOP 1 CAST(value AS NVARCHAR(MAX)) FROM sys.extended_properties
       WHERE class = 1 AND major_id = t.object_id AND minor_id = 0 AND name = 'MS_Description') AS comment
    FROM sys.tables t
    JOIN sys.schemas s ON t.schema_id = s.schema_id
    ${scope.from}WHERE s.name = ${scope.schemaRef}
      AND t.name = ${scope.tableRef};
  `;

const columnEnrichmentsBody = (scope: TableScope): string => `
    SELECT${scope.select}
      c.name AS column_name,
      c.is_computed,
      cc.definition AS computed_definition,
      cc.is_persisted,
      ic.seed_value,
      ic.increment_value,
      c.collation_name,
      (SELECT TOP 1 CAST(value AS NVARCHAR(MAX)) FROM sys.extended_properties
       WHERE class = 1 AND major_id = c.object_id AND minor_id = c.column_id AND name = 'MS_Description') AS comment
    FROM sys.columns c
    JOIN sys.tables t ON c.object_id = t.object_id
    JOIN sys.schemas s ON t.schema_id = s.schema_id
    LEFT JOIN sys.computed_columns cc ON cc.object_id = c.object_id AND cc.column_id = c.column_id
    LEFT JOIN sys.identity_columns ic ON ic.object_id = c.object_id AND ic.column_id = c.column_id
    ${scope.from}WHERE s.name = ${scope.schemaRef}
      AND t.name = ${scope.tableRef};
  `;

const partitionBody = (scope: TableScope): string => `
    SELECT${scope.select}
      pf.name AS function_name,
      ps.name AS scheme_name,
      c.name AS column_name,
      ic.partition_ordinal,
      CASE pf.boundary_value_on_right WHEN 0 THEN 'RANGE' ELSE 'RANGE' END AS strategy
    FROM sys.tables t
    JOIN sys.indexes i ON t.object_id = i.object_id AND i.index_id IN (0, 1)
    JOIN sys.partition_schemes ps ON i.data_space_id = ps.data_space_id
    JOIN sys.partition_functions pf ON ps.function_id = pf.function_id
    JOIN sys.index_columns ic ON ic.object_id = i.object_id AND ic.index_id = i.index_id AND ic.partition_ordinal > 0
    JOIN sys.columns c ON c.object_id = ic.object_id AND c.column_id = ic.column_id
    JOIN sys.schemas s ON t.schema_id = s.schema_id
    ${scope.from}WHERE s.name = ${scope.schemaRef}
      AND t.name = ${scope.tableRef}
    ORDER BY ${scope.orderBy}ic.partition_ordinal;
  `;

/**
 * The six table-scoped catalog reads a schema compare needs, rendered for a
 * SET of tables instead of one: the same bodies as the per-table builders in
 * `mssqlQueries` below, joined to a `VALUES` row constructor of the requested
 * tables. Every row carries `table_schema` / `table_name` (the requested
 * strings, verbatim) so the caller can group rows back per table and hand
 * each group to the row→model mapping the per-table path uses.
 *
 * NOT wrapped in `sp_executesql`: each chunk's text is unique by construction
 * (it names its tables), so there is no plan to share and nothing to gain —
 * the win here is round trips (6 per 300 tables instead of 6 per table), not
 * compilations. Quick depth reads `columns` + `columnEnrichments` only,
 * mirroring `getTableMetadata`'s light mode.
 */
export const mssqlBulkTableQueries = {
  columns: (tables: readonly MssqlTableIdentity[]): string => columnsBody(bulkTableScope(tables)),
  tableConstraints: (tables: readonly MssqlTableIdentity[]): string => tableConstraintsBody(bulkTableScope(tables)),
  tableIndexes: (tables: readonly MssqlTableIdentity[]): string => tableIndexesBody(bulkTableScope(tables)),
  tableOptions: (tables: readonly MssqlTableIdentity[]): string => tableOptionsBody(bulkTableScope(tables)),
  columnEnrichments: (tables: readonly MssqlTableIdentity[]): string => columnEnrichmentsBody(bulkTableScope(tables)),
  partition: (tables: readonly MssqlTableIdentity[]): string => partitionBody(bulkTableScope(tables)),
};

export type MssqlBulkTableQueryFamily = keyof typeof mssqlBulkTableQueries;

export const mssqlQueries = {
  /**
   * Get all databases. `HAS_DBACCESS(name)` (1 = accessible, 0 = no access,
   * NULL = offline/invalid) lets the Object Explorer still list every
   * database while the background object-preload only targets databases the
   * login can actually open — see `databasesService` `accessibleDatabases`.
   */
  databases: `SELECT name, HAS_DBACCESS(name) AS has_access FROM sys.databases ORDER BY name;`,

  /**
   * Schemas a table can be created in, read from the catalog rather than
   * derived from the table listing: a database with no tables yet still has
   * `dbo`, and Data Import's Target step must offer it before the first table
   * exists. Excludes the fixed-role schemas (`db_owner`…, schema_id
   * 16384–16399) and the system schemas nothing can be created in.
   */
  schemas: `
    SELECT s.name AS name
    FROM sys.schemas s
    WHERE s.schema_id NOT BETWEEN 16384 AND 16399
      AND s.name NOT IN ('sys', 'INFORMATION_SCHEMA', 'guest')
    ORDER BY s.name;
  `,

  /** Get tables for a database */
  tables: `
    SELECT
      t.object_id AS id,
      t.name,
      s.name AS schema_name,
      'table' AS object_type
    FROM
      sys.tables t
    JOIN
      sys.schemas s ON t.schema_id = s.schema_id
    ORDER BY
      s.name, t.name;
  `,

  /** Get views for a database */
  views: `
    SELECT
      v.object_id AS id,
      v.name,
      s.name AS schema_name,
      'view' AS object_type
    FROM
      sys.views v
    JOIN
      sys.schemas s ON v.schema_id = s.schema_id
    ORDER BY
      s.name, v.name;
  `,

  /** Get stored procedures for a database */
  storedProcedures: `
    SELECT
      p.object_id AS id,
      p.name,
      s.name AS schema_name,
      'stored-procedure' AS object_type
    FROM
      sys.procedures p
    JOIN
      sys.schemas s ON p.schema_id = s.schema_id
    WHERE
      p.is_ms_shipped = 0
    ORDER BY
      s.name, p.name;
  `,

  /** Get functions for a database */
  functions: `
    SELECT
      o.object_id AS id,
      o.name,
      s.name AS schema_name,
      'function' AS object_type
    FROM
      sys.objects o
    JOIN
      sys.schemas s ON o.schema_id = s.schema_id
    WHERE
      o.type IN ('FN', 'IF', 'TF', 'AF')
      AND o.is_ms_shipped = 0
    ORDER BY
      s.name, o.name;
  `,

  /**
   * Get user-defined TABLE types (Programmability -> Types -> User-Defined
   * Table Types). `sys.table_types` only ever holds table types, but the
   * `is_user_defined = 1` filter keeps the system-shipped ones (the internal
   * `sys.*` table types SQL Server creates for its own features) out of the
   * listing, exactly like `is_ms_shipped = 0` does for procedures above.
   */
  tableTypes: `
    SELECT
      tt.type_table_object_id AS id,
      tt.name,
      s.name AS schema_name,
      'table-type' AS object_type
    FROM
      sys.table_types tt
    JOIN
      sys.schemas s ON tt.schema_id = s.schema_id
    WHERE
      tt.is_user_defined = 1
    ORDER BY
      s.name, tt.name;
  `,

  /**
   * Get user-defined ALIAS types (Programmability -> Types -> User-Defined
   * Data Types) — `CREATE TYPE dbo.Clave FROM nvarchar(50) NOT NULL`.
   *
   * The base system type is joined in so the row carries everything the
   * CREATE TYPE script needs. `max_length` is in BYTES for the N-types, which
   * is why the script generator halves it (see `formatColumnDataType`).
   */
  aliasTypes: `
    SELECT
      t.user_type_id AS id,
      t.name,
      s.name AS schema_name,
      'alias-type' AS object_type,
      bt.name AS base_type_name,
      t.max_length,
      t.precision,
      t.scale,
      t.is_nullable
    FROM
      sys.types t
    JOIN
      sys.schemas s ON t.schema_id = s.schema_id
    LEFT JOIN
      sys.types bt ON bt.user_type_id = t.system_type_id AND bt.is_user_defined = 0
    WHERE
      t.is_user_defined = 1
      AND t.is_table_type = 0
      AND t.is_assembly_type = 0
    ORDER BY
      s.name, t.name;
  `,

  /**
   * Get user-defined CLR types (Programmability -> Types -> User-Defined Types
   * (CLR)). The assembly + class come from `sys.assembly_types`, which is what
   * `CREATE TYPE ... EXTERNAL NAME [assembly].[class]` is rebuilt from.
   */
  clrTypes: `
    SELECT
      t.user_type_id AS id,
      t.name,
      s.name AS schema_name,
      'clr-type' AS object_type,
      a.name AS assembly_name,
      at.assembly_class AS assembly_class
    FROM
      sys.types t
    JOIN
      sys.schemas s ON t.schema_id = s.schema_id
    LEFT JOIN
      sys.assembly_types at ON at.user_type_id = t.user_type_id
    LEFT JOIN
      sys.assemblies a ON a.assembly_id = at.assembly_id
    WHERE
      t.is_user_defined = 1
      AND t.is_assembly_type = 1
    ORDER BY
      s.name, t.name;
  `,

  /**
   * Get columns for a table or view
   * @param schema - Schema name (will be escaped)
   * @param tableName - Object name (table/view) (will be escaped)
   */
  columns: perTableQuery(columnsBody(PER_TABLE_SCOPE)),

  /**
   * Get table constraints for a table
   *
   * Every INFORMATION_SCHEMA join here must carry the CONSTRAINT_SCHEMA as well
   * as the CONSTRAINT_NAME: SQL Server scopes constraint names per SCHEMA, not
   * per database, so two schemas may each hold their own `FK_Child_Parent`. On
   * name alone the `rc` and `cc` joins fan out across schemas, which showed up
   * as a CHECK constraint reporting another schema's clause and a foreign key
   * pointing at the wrong schema's table (#116).
   *
   * The CHECK body comes from `sys.check_constraints.definition` (nvarchar(max))
   * rather than `INFORMATION_SCHEMA.CHECK_CONSTRAINTS.CHECK_CLAUSE`, which is
   * nvarchar(4000) and silently TRUNCATES longer bodies — measured at 4000 of
   * 7273 characters on a real instance. A truncated CHECK clause is not merely
   * incomplete, it is invalid SQL, and it reaches Schema Compare and the
   * Blueprint's generated DDL (#116).
   *
   * @param schema - Schema name (will be escaped)
   * @param tableName - Table name (will be escaped)
   */
  tableConstraints: perTableQuery(tableConstraintsBody(PER_TABLE_SCOPE)),

  /**
   * Get table indexes for a table
   * @param schema - Schema name (will be escaped)
   * @param tableName - Table name (will be escaped)
   */
  tableIndexes: perTableQuery(tableIndexesBody(PER_TABLE_SCOPE)),

  /**
   * Get default constraints for a table
   * @param schema - Schema name (will be escaped)
   * @param tableName - Table name (will be escaped)
   */
  tableDefaultConstraints: perTableQuery(`
    SELECT
      dc.name AS constraint_name,
      'DEFAULT' AS constraint_type,
      COL_NAME(dc.parent_object_id, dc.parent_column_id) AS column_name,
      dc.definition AS default_definition
    FROM sys.default_constraints dc
    JOIN sys.tables t ON dc.parent_object_id = t.object_id
    JOIN sys.schemas s ON t.schema_id = s.schema_id
    WHERE
      s.name = @schema
      AND t.name = @table
    ORDER BY dc.name;
  `),

  /**
   * Get table triggers for a table
   * @param schema - Schema name (will be escaped)
   * @param tableName - Table name (will be escaped)
   */
  tableTriggers: perTableQuery(`
    SELECT
      tr.name AS trigger_name,
      CASE WHEN tr.is_instead_of_trigger = 1 THEN 'INSTEAD OF' ELSE 'AFTER' END AS trigger_timing,
      STUFF((
        SELECT ', ' + te_inner.type_desc
        FROM sys.trigger_events te_inner
        WHERE te_inner.object_id = tr.object_id
        FOR XML PATH(''), TYPE).value('.', 'NVARCHAR(MAX)'), 1, 2, '') AS trigger_events,
      CASE WHEN tr.is_disabled = 1 THEN 0 ELSE 1 END AS is_enabled
    FROM sys.triggers tr
    JOIN sys.tables t ON tr.parent_id = t.object_id
    JOIN sys.schemas s ON t.schema_id = s.schema_id
    WHERE
      s.name = @schema
      AND t.name = @table
    ORDER BY tr.name;
  `),

  /**
   * Get table statistics for a table
   * @param schema - Schema name (will be escaped)
   * @param tableName - Table name (will be escaped)
   */
  tableStatistics: perTableQuery(`
    SELECT
      s.name AS statistic_name,
      COL_NAME(sc.object_id, sc.column_id) AS column_name,
      sc.stats_column_id AS column_ordinal,
      s.auto_created,
      s.user_created,
      STATS_DATE(s.object_id, s.stats_id) AS last_updated
    FROM sys.stats s
    JOIN sys.stats_columns sc ON s.object_id = sc.object_id AND s.stats_id = sc.stats_id
    JOIN sys.tables t ON s.object_id = t.object_id
    JOIN sys.schemas sch ON t.schema_id = sch.schema_id
    WHERE
      sch.name = @schema
      AND t.name = @table
    ORDER BY s.name, sc.stats_column_id;
  `),

  /**
   * Get table-level options + comment (one row expected)
   * @param schema - Schema name (will be escaped)
   * @param tableName - Table name (will be escaped)
   */
  tableOptions: perTableQuery(tableOptionsBody(PER_TABLE_SCOPE)),

  /**
   * Get per-column extras: computed expression, identity seed/increment, collation, comment.
   * @param schema - Schema name (will be escaped)
   * @param tableName - Table name (will be escaped)
   */
  columnEnrichments: perTableQuery(columnEnrichmentsBody(PER_TABLE_SCOPE)),

  /**
   * Get partition definition for a table (zero or more rows).
   * @param schema - Schema name (will be escaped)
   * @param tableName - Table name (will be escaped)
   */
  partition: perTableQuery(partitionBody(PER_TABLE_SCOPE)),

  /**
   * Get database properties
   *
   * `N'…'` for the same reason the per-table builders pass `sysname`
   * parameters (see `perTableQuery`): an unprefixed literal is varchar, read in
   * the database's default collation code page, so a database named outside
   * that code page degrades to `?` and this returns NO row at all — an empty
   * properties panel rather than an error. Runs once per compare, so there is
   * nothing to gain from wrapping it in `sp_executesql` as well; the Unicode
   * correctness is the whole point here.
   *
   * @param databaseName - Database name (will be escaped)
   */
  databaseProperties: (databaseName: string) => `
    SELECT
      d.name AS database_name,
      d.collation_name AS collation,
      sp.name AS owner,
      d.recovery_model_desc AS recovery_model,
      d.compatibility_level
    FROM
      sys.databases d
    LEFT JOIN
      sys.server_principals sp ON d.owner_sid = sp.sid
    WHERE
      d.name = N'${quote(databaseName)}';
  `,

  /** Default database for MSSQL */
  defaultDatabase: 'master',

  /** Fallback databases if query fails */
  fallbackDatabases: ['master', 'model', 'msdb', 'tempdb'],
};
